'use client';

import { useEffect, useCallback } from 'react';

import { isTrackingAllowed } from '@/utils/nativeBridge';

declare global {
  interface Window {
    permutive: any;
    googletag: any;
  }
}

interface PermutiveConfig {
  apiKey: string;
  workspaceId: string;
  environment?: string;
}

interface PermutivePageData {
  section?: string;
  kw_edad?: number;
  kw_sexo?: string;
  kw_equipo?: string;
  kw_premium?: boolean;
  kw_escuela?: string;
  kw_primaria?: boolean;
  kw_secundario?: boolean;
  kw_recibirinfo?: boolean;
  kw_pines?: number;
  kw_saldo?: number;
  kw_torneoamigospremios?: boolean;
  kw_fechaoro?: boolean;
  kw_medallas?: number;
  kw_fecha?: number;
  kw_puntosporfecha?: number;
  kw_puntosportorneo?: number;
  kw_posicionporfecha?: number;
  kw_posicionportorneo?: number;
}

const DEFAULT_CONFIG: PermutiveConfig = {
  apiKey: '07a55b4b-dafd-4aca-a5f7-d898e8150bd4',
  workspaceId: '85f0535a-ef9b-4e23-868d-1c56324cbe0a',
  environment: 'production',
};

export const usePermutive = (config: Partial<PermutiveConfig> = {}) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const initializePermutive = useCallback(() => {
    // eslint-disable-next-line no-console
    // console.info('// Initialize Permutive if not already done');
    if (!window.permutive) {
      const generateViewId = (): string => {
        return (1e7 + '' + 1e3 + 4e3 + 8e3 + 1e11).replace(
          /[018]/g,
          (c: string) => {
            const num = parseInt(c, 10);
            const cryptoObj = window.crypto || (window as any).msCrypto;

            if (!cryptoObj?.getRandomValues) {
              return (num ^ ((Math.random() * 16) >> (num / 4))).toString(16);
            }

            const randomArray = new Uint8Array(1);
            cryptoObj.getRandomValues(randomArray);

            return (num ^ (randomArray[0] & (15 >> (num / 4)))).toString(16);
          }
        );
      };

      window.permutive = window.permutive || { q: [] };
      window.permutive.config = finalConfig;

      if (window.crypto || (window as any).msCrypto) {
        window.permutive.config.viewId = generateViewId();
      }

      const methods = [
        'addon',
        'identify',
        'track',
        'trigger',
        'query',
        'segment',
        'segments',
        'ready',
        'on',
        'once',
        'user',
        'consent',
      ];
      methods.forEach((method) => {
        window.permutive[method] = (...args: any[]) => {
          window.permutive.q.push({ functionName: method, arguments: args });
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupGoogleTagTargeting = useCallback(() => {
    // window.googletag = {};
    if (!window.googletag || !window.googletag.cmd) {
      window.googletag = { cmd: [] };
    }
    window.googletag.cmd.push(() => {
      if (window.googletag.pubads().getTargeting('permutive').length === 0) {
        // Set Permutive targeting from localStorage
        const kvs = window.localStorage.getItem('_pdfps');
        window.googletag
          .pubads()
          .setTargeting('permutive', kvs ? JSON.parse(kvs) : []);

        // Set user ID targeting
        const permutiveId = window.localStorage.getItem('permutive-id');
        if (permutiveId) {
          window.googletag.pubads().setTargeting('puid', permutiveId);
          window.googletag
            .pubads()
            .setTargeting('ptime', Date.now().toString());
        }

        // Set view ID and workspace ID targeting
        if (window.permutive?.config?.viewId) {
          window.googletag
            .pubads()
            .setTargeting('prmtvvid', window.permutive.config.viewId);
        }

        if (window.permutive?.config?.workspaceId) {
          window.googletag
            .pubads()
            .setTargeting('prmtvwid', window.permutive.config.workspaceId);
        }
      }
    });
  }, []);

  const trackPageView = useCallback((pageData: PermutivePageData = {}) => {
    if (!window.permutive) return;

    // eslint-disable-next-line no-console
    // console.info('// Track page view with data:', pageData);

    const defaultPageData = {
      tecnico: {
        cl_height: window.innerHeight,
        cl_width: window.innerWidth,
      },
      ...pageData,
    };

    window.permutive.addon('web', {
      page: defaultPageData,
    });
  }, []);

  const loadPermutiveScript = useCallback(() => {
    if (document.getElementById('permutive-script')) return;

    const script = document.createElement('script');
    script.id = 'permutive-script';
    script.async = true;
    script.src = `https://07652d0f-3e99-4279-ab32-950ddf1523f0.edge.permutive.app/${finalConfig.workspaceId}-web.js`;

    document.head.appendChild(script);
  }, [finalConfig.workspaceId]);

  useEffect(() => {
    initializePermutive();
    // App iOS sin permiso ATT: queda solo el stub (trackPageView encola y no envía nada)
    if (!isTrackingAllowed()) return;
    setupGoogleTagTargeting();
    loadPermutiveScript();
  }, [initializePermutive, setupGoogleTagTargeting, loadPermutiveScript]);

  return {
    trackPageView,
    isReady: !!window.permutive,
  };
};
